import React, { useState, useEffect, useRef } from "react";
import topTost from "../../../utils/topTost";
import Dropdown from "../Dropdown";
import { useLocation } from "react-router-dom";
import { DISABLE_COLUMN_VISIBILITY_FOR } from "./tableConfig";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const PermiTableSearch = ({
  table,
  setGlobalFilter,
  globalFilter,
  setColumnVisibility,
}) => {
  const [selectAll, setSelectAll] = useState(false);
  const dropdownRef = useRef();
  const location = useLocation();
  const [pdfColumns, setPdfColumns] = useState([]);
  const [showPdfModal, setShowPdfModal] = useState(false);

  const disableColumnVisibility =
    DISABLE_COLUMN_VISIBILITY_FOR[location.pathname] ?? false;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Optional: close dropdown if needed
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleableColumns = table
    .getAllLeafColumns()
    .filter((col) => table.getColumn(col.id)?.getCanHide());

  const copyToClipboard = () => {
    const columns = table
      .getVisibleLeafColumns()
      .filter((column) => column.id.toLowerCase() !== "actions");
    const headers = columns.map((column) =>
      typeof column.columnDef.header === "function"
        ? column.columnDef.header()
        : column.columnDef.header
    );
    const rows = table.getRowModel().rows.map((row) =>
      row
        .getVisibleCells()
        .filter((cell) => cell.column.id.toLowerCase() !== "actions")
        .map((cell) => cell.getValue())
    );

    const content =
      "Exported Table\n\n" +
      [headers, ...rows].map((row) => row.join("\t")).join("\n");

    navigator.clipboard.writeText(content).then(() => {
      topTost({ title: "Copied to clipboard!", icon: "success" });
    });
  };

  const allSelected = toggleableColumns.every((col) => col.getIsVisible());

  useEffect(() => {
    setSelectAll(allSelected);
  }, [allSelected]);

  const columnItems = [
    {
      id: "select_all",
      label: selectAll ? "Select All" : "Deselect All",
      checked: selectAll,
      checkbox: true,
      isSelectAll: true,
    },

    ...toggleableColumns.map((col) => {
      const header = col.columnDef.header;
      return {
        id: col.id,
        label: typeof header === "function" ? header() : header,
        checked: col.getIsVisible(),
        checkbox: true,
      };
    }),
  ];

  const handleCheckboxChange = (columnId, isChecked) => {
    if (columnId === "select_all") {
      const updatedVisibility = toggleableColumns.reduce((acc, col) => {
        acc[col.id] = isChecked;
        return acc;
      }, {});
      setColumnVisibility(updatedVisibility);

    } else {
      setColumnVisibility((prevVisibility) => {
        const updated = {
          ...prevVisibility,
          [columnId]: isChecked,
        };
        return updated;
      });
    }
  };


  const exportToPDF = (selectedColumnIds) => {
    const doc = new jsPDF();

    const currentPath = location.pathname.split("/").pop();
    const formattedPageName = currentPath
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

    const now = new Date();
    const formattedDateTime = now
      .toLocaleString("en-IN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(/[/,]/g, "-")
      .replace(/\s+/g, "_");

    const fileName = `${formattedPageName}_${formattedDateTime}.pdf`;

    const selectedColumns = table
      .getAllLeafColumns()
      .filter(
        (col) =>
          selectedColumnIds.includes(col.id) &&
          col.id.toLowerCase() !== "actions"
      );

    const headers = [
      "S.NO",
      ...selectedColumns.map((col) => {
        const header =
          typeof col.columnDef.header === "function"
            ? col.columnDef.header()
            : col.columnDef.header;
        return String(header).toUpperCase();
      }),
    ];

    const rows = table.getRowModel().rows.map((row, index) => {
      const rowData = selectedColumns.map((col) => {
        const cell = row.getAllCells().find((c) => c.column.id === col.id);
        const value = cell?.getValue();
        return typeof value === "string" ? value.toUpperCase() : value;
      });
      return [index + 1, ...rowData];
    });

    autoTable(doc, {
      head: [headers],
      body: rows,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 160, 133] },
    });

    doc.save(fileName);
  };

  const exportToExcel = () => {
    const columns = table
      .getVisibleLeafColumns()
      .filter((col) => col.id.toLowerCase() !== "actions");
    const headers = columns.map((col) =>
      typeof col.columnDef.header === "function"
        ? col.columnDef.header()
        : col.columnDef.header
    );
    const rows = table.getRowModel().rows.map((row) =>
      row
        .getVisibleCells()
        .filter((cell) => cell.column.id.toLowerCase() !== "actions")
        .map((cell) => cell.getValue())
    );

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Export");
    XLSX.writeFile(workbook, "Exported-Data.xlsx");
  };

  return (
    <>
      <div className="row gy-2 position-relative">
        <div className="col-sm-12 col-md-6 ps-0 m-0 pb-10">
          <div className="d-flex flex-wrap gap-2 align-items-center justify-content-md-start justify-content-center">
            {!disableColumnVisibility && (
              <div className="d-flex gap-2 position-relative" ref={dropdownRef}>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={copyToClipboard}
                >
                  Copy
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowPdfModal(true)}
                >
                  PDF
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={exportToExcel}
                >
                  Excel
                </button>
                <Dropdown
                  dropdownItems={columnItems}
                  triggerIcon={
                    <span className="fw-semibold">Column Visibility</span>
                  }
                  triggerPosition="0,22"
                  triggerClass="btn btn-outline-secondary btn-sm"
                  dropdownAutoClose="outside"
                  tooltipTitle="Toggle Columns"
                  onCheckboxChange={(columnId, isChecked) =>
                    handleCheckboxChange(columnId, isChecked)
                  }
                  isAvatar={false}
                  disableColumnVisibility={disableColumnVisibility}
                />
              </div>
            )}
            <div className="dataTables_length d-flex align-items-center">
              <label className="d-flex align-items-center gap-2 m-0 m-1">
                Show
                <select
                  className="form-select form-select-sm w-auto pe-4"
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                >
                  {[100, 200].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize}
                    </option>
                  ))}
                </select>
                entries
              </label>
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-6 ps-0 m-0 pb-10">
          <div className="dataTables_filter d-flex justify-content-md-end justify-content-center">
            <label className="d-inline-flex align-items-center gap-2 m-0">
              Search:
              <input
                type="text"
                value={globalFilter ?? ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder="Search..."
                className="form-control form-control-sm"
              />
            </label>
          </div>
        </div>
      </div>

      {showPdfModal && (
        <div
          className="modal d-block"
          tabIndex={-1}
          role="dialog"
          style={{ backdropFilter: "blur(4px)" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Columns for PDF</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPdfModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {table
                  .getAllLeafColumns()
                  .filter((col) => col.columnDef?.meta?.exportable !== false)
                  .map((col) => {
                    const header =
                      typeof col.columnDef.header === "function"
                        ? col.columnDef.header().toUpperCase()
                        : col.columnDef.header.toUpperCase();
                    return (
                      <div key={col.id} className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`pdf-col-${col.id}`}
                          checked={pdfColumns.includes(col.id)}
                          onChange={() =>
                            setPdfColumns((prev) =>
                              prev.includes(col.id)
                                ? prev.filter((id) => id !== col.id)
                                : [...prev, col.id]
                            )
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`pdf-col-${col.id}`}
                        >
                          {header}
                        </label>
                      </div>
                    );
                  })}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPdfModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    exportToPDF(pdfColumns);
                    setShowPdfModal(false);
                  }}
                >
                  Export PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PermiTableSearch;
